import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthentificationService } from '@app/authentification/authentification.service';
import { HttpApiService } from '@app/providers/http-api/http-api.service';
import { AssetTypeService } from '@app/services/asset-type.service';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import { combineLatest, forkJoin, Observable, of, OperatorFunction, Subject, map, mergeMap, switchMap, tap } from 'rxjs';

import { Dao } from '../dao';
import { DataLoader } from '../data-loader';
import { DataLoaderAuthentified } from '../data-loader-authentified';
import { UserDao } from '../user/user.dao';
import { DataAssets } from './assets.type';

export class AssetProvidersCriteria {
  page?: number;
  size?: number;
  distinct?: boolean;
  sorts?: string[];
  fetches?: string[];
  id?: number;
  name?: string;
  code?: string;
  autocomplete?: string;
  productType?: string;
  productCategory?: string;
  status?: string[];
}
@Injectable({
  providedIn: 'root',
})
export class AssetDao extends Dao {
  constructor(
    httpApi: HttpApiService,
    authentificationService: AuthentificationService,
    private userDao: UserDao,
    private translateService: TranslateService,
    private assetTypeService: AssetTypeService,
  ) {
    super(httpApi, authentificationService);
  }

  pipe(arg0: OperatorFunction<unknown, any>) {
    throw new Error('Method not implemented.');
  }

  private references: DataLoader<any> = new DataLoader<any>(() =>
    forkJoin([this.getSubscriptionSources()]),
  );

  private desficAssets: DataLoader<any[]> = new DataLoader<any[]>(() =>
    this.userDao.getCouple().pipe(
      switchMap((couple: any) => {
        let omittedAsset = ['PME'];
        let subs = [
          this.httpApi
            .get(
              `/user/${couple[0].id}/assets/defiscalisation/global?current=ALL`,
            )
            .pipe(
              map(res => {
                res['data']['groupedAssetList'] = _.filter(
                  res['data']['groupedAssetList'],
                  asset => !omittedAsset.includes(asset.type),
                );
                return res;
              }),
              tap(res => (res['data'].accountId = couple[0].accountId)),
            ),
        ];
        //Check if couple memeber exists
        if (couple[1])
          subs.push(
            this.httpApi
              .get(
                `/user/${couple[1].id}/assets/defiscalisation/global?current=ALL`,
              )
              .pipe(
                map(res => {
                  res['data']['groupedAssetList'] = _.filter(
                    res['data']['groupedAssetList'],
                    asset => !omittedAsset.includes(asset.type),
                  );
                  return res;
                }),
                tap(res => (res['data'].accountId = couple[1].accountId)),
              ),
          );
        return forkJoin(subs);
      }),

      tap(res => {
        _(res[0]['data']['groupedAssetList'])
          .map('list')
          .flatten()
          .forEach(listItem => (listItem.accountId = res[0]['data'].accountId));
        if (res[1])
          _(res[1]['data']['groupedAssetList'])
            .map('list')
            .flatten()
            .forEach(
              listItem => (listItem.accountId = res[1]['data'].accountId),
            );
      }),
    ),
  );

  // private riskScore: DataLoaderAuthentified<any[]> =
  //   new DataLoaderAuthentified<any[]>(this.getAuthentified(), user => {
  //     return this.httpApi.get(`/accounts/${user.accountId}/assets/risk-score`);
  // });

  private assets: DataLoaderAuthentified<DataAssets> =
    new DataLoaderAuthentified<DataAssets>(this.getAuthentifiedMainUserOrMainUserFromAuthentifiedSpouse(), user => {
      return this.httpApi
        .get(`/user/${user.id}/assets/global?current=ALL`)
        .pipe(map(res => res['data']));
    });

  private currentAssets: DataLoaderAuthentified<DataAssets> =
    new DataLoaderAuthentified<DataAssets>(this.getAuthentifiedMainUserOrMainUserFromAuthentifiedSpouse(), user => {
      return this.httpApi
        .get(`/user/${user.id}/assets/global?current=ISCURRENT`)
        .pipe(map(res => res['data']));
    });

  private tangibleAssets: DataLoaderAuthentified<any[]> =
    new DataLoaderAuthentified<any[]>(this.getAuthentifiedMainUserOrMainUserFromAuthentifiedSpouse(), user => {
      return this.httpApi.get(
        `/user/${user.id}/assets/tangible/global?current=ALL`,
      );
    });

  private perAssets: DataLoaderAuthentified<any[]> = new DataLoaderAuthentified<
    any[]
  >(this.getAuthentifiedMainUserOrMainUserFromAuthentifiedSpouse(), user => {
    return this.httpApi.get(`/user/${user.id}/assets/per/global?current=ALL`);
  });

  private perpAssets: DataLoaderAuthentified<any[]> =
    new DataLoaderAuthentified<any[]>(this.getAuthentifiedMainUserOrMainUserFromAuthentifiedSpouse(), user => {
      return this.httpApi.get(
        `/user/${user.id}/assets/perp/global?current=ALL`,
      );
    });

  private prefonAssets: DataLoaderAuthentified<any[]> =
    new DataLoaderAuthentified<any[]>(this.getAuthentifiedMainUserOrMainUserFromAuthentifiedSpouse(), user => {
      return this.httpApi.get(
        `/user/${user.id}/assets/prefon/global?current=ALL`,
      );
    });

  private percoAssets: DataLoaderAuthentified<any[]> =
    new DataLoaderAuthentified<any[]>(this.getAuthentifiedMainUserOrMainUserFromAuthentifiedSpouse(), user => {
      return this.httpApi.get(
        `/user/${user.id}/assets/perco/global?current=ALL`,
      );
    });

  private peeAssets: DataLoaderAuthentified<any[]> = new DataLoaderAuthentified<
    any[]
  >(this.getAuthentifiedMainUserOrMainUserFromAuthentifiedSpouse(), user => {
    return this.httpApi.get(`/user/${user.id}/assets/pee/global?current=ALL`);
  });

  private peaAssets: DataLoaderAuthentified<any[]> = new DataLoaderAuthentified<
    any[]
  >(this.getAuthentifiedMainUserOrMainUserFromAuthentifiedSpouse(), user => {
    return this.httpApi.get(`/user/${user.id}/assets/pea/global?current=ALL`);
  });

  private peaPmeAssets: DataLoaderAuthentified<any[]> =
    new DataLoaderAuthentified<any[]>(this.getAuthentifiedMainUserOrMainUserFromAuthentifiedSpouse(), user => {
      return this.httpApi.get(
        `/user/${user.id}/assets/peaPme/global?current=ALL`,
      );
    });

  private compteTitreAssets: DataLoaderAuthentified<any[]> =
    new DataLoaderAuthentified<any[]>(this.getAuthentifiedMainUserOrMainUserFromAuthentifiedSpouse(), user => {
      return this.httpApi.get(
        `/user/${user.id}/assets/compteTitre/global?current=ALL`,
      );
    });

  private compteCryptoAssets: DataLoaderAuthentified<any[]> =
    new DataLoaderAuthentified<any[]>(this.getAuthentifiedMainUserOrMainUserFromAuthentifiedSpouse(), user => {
      return this.httpApi.get(
        `/user/${user.id}/assets/compteCrypto/global?current=ALL`,
      );
    });

  private crowdfundingAssets: DataLoaderAuthentified<any[]> =
    new DataLoaderAuthentified<any[]>(this.getAuthentifiedMainUserOrMainUserFromAuthentifiedSpouse(), user => {
      return this.httpApi.get(
        `/user/${user.id}/assets/crowdfunding/global?current=ALL`,
      );
    });

  private pmeAssets: DataLoaderAuthentified<any[]> = new DataLoaderAuthentified<
    any[]
  >(this.getAuthentifiedMainUserOrMainUserFromAuthentifiedSpouse(), user => {
    return this.httpApi.get(`/user/${user.id}/assets/pme/global?current=ALL`);
  });

  private othersAssets: DataLoaderAuthentified<any[]> = new DataLoaderAuthentified<
    any[]
  >(this.getAuthentifiedMainUserOrMainUserFromAuthentifiedSpouse(), user => {
    return this.httpApi.get(`/user/${user.id}/assets/others?current=ALL`);
  });

  private lifeInsurranceAssets: DataLoaderAuthentified<any[]> =
    new DataLoaderAuthentified<any[]>(this.getAuthentifiedMainUserOrMainUserFromAuthentifiedSpouse(), user => {
      return this.httpApi.get(
        `/user/${user.id}/assets/assuranceVie/global?current=ALL`,
      );
    });

  private lifeInsurranceAssetsGlobal: DataLoaderAuthentified<any[]> =
    new DataLoaderAuthentified<any[]>(this.getAuthentifiedMainUserOrMainUserFromAuthentifiedSpouse(), user => {
      return this.httpApi.get(
        `/user/${user.id}/assets/assuranceVie/global?current=ALL`,
      );
    });

  private contratCapiGlobal: DataLoaderAuthentified<any[]> =
    new DataLoaderAuthentified<any[]>(this.getAuthentifiedMainUserOrMainUserFromAuthentifiedSpouse(), user => {
      return this.httpApi.get(
        `/user/${user.id}/assets/contratCapi/global?current=ALL`,
      );
    });

  private bankResourcesAssets: DataLoaderAuthentified<any[]> =
    new DataLoaderAuthentified<any[]>(this.getAuthentifiedMainUserOrMainUserFromAuthentifiedSpouse(), user => {
      return this.httpApi.get(`/user/${user.id}/assets/banking`);
    });

  getRiskScore(user, types?: string[]): Observable<any> {
    let params = new HttpParams();

    if (types) {
      types.forEach(type => {
        params = params.append('types', type);
      });
    }

    const queryParams = params.toString();

    return this.httpApi.get(
      `/accounts/${user.accountId}/assets/risk-score?${queryParams}`,
    );
  }

  getAssets(): Observable<DataAssets> {
    return this.assets.get();
  }

  getCurrentAssets(): Observable<DataAssets> {
    return this.currentAssets.get();
  }

  getDesficAssets(id?): Observable<any> {
    return this.desficAssets.get();
  }

  getTangibleAssets(): Observable<any> {
    return this.tangibleAssets.get();
  }

  getPerAssets(): Observable<any> {
    return this.perAssets.get();
  }

  getPerpAssets(): Observable<any> {
    return this.perpAssets.get();
  }

  getPercoAssets(): Observable<any> {
    return this.percoAssets.get();
  }

  getPrefonAssets(): Observable<any> {
    return this.prefonAssets.get();
  }

  getPeeAssets(): Observable<any> {
    return this.peeAssets.get();
  }

  getPeaAssets(): Observable<any> {
    return this.peaAssets.get();
  }

  getPeaPmeAssets(): Observable<any> {
    return this.peaPmeAssets.get();
  }

  getCompteTitreAssets(): Observable<any> {
    return this.compteTitreAssets.get();
  }

  getCompteCryptoAssets(): Observable<any> {
    return this.compteCryptoAssets.get();
  }

  getCrowdfundingAssets(): Observable<any> {
    return this.crowdfundingAssets.get();
  }

  getPmeAssets(): Observable<any> {
    return this.pmeAssets.get();
  }

  getOthersAssets(): Observable<any> {
    return this.othersAssets.get();
  }

  getBankResourcesAssets(): Observable<any> {
    return this.bankResourcesAssets.get();
  }

  saveAssets(entity: any, reset: boolean = true): Observable<any> {
    let dto = _.cloneDeep(entity);
    let typeUrl = '' + _.camelCase(entity.type);
    if (
      this.assetTypeService
        .getTaxExemptingAssetTypeFipFCPI()
        .includes(entity.type)
    )
      typeUrl = 'fipfcpi';
    else if (entity.type.toUpperCase() === 'FPS') typeUrl = 'others';
    // else dto.type = 'ASSETS';

    let editObservable = () => {
      //dto = { ..._.omit(dto, ["id"]) };
      return this.httpApi.post(
        `/user/${entity.userId}/assets/${typeUrl}/${entity.id}`,
        dto,
      );
    };
    let createObservable = () =>
      this.httpApi
        .post(`/accounts/${entity.accountId}/subscriptions`, dto)
        .pipe(
          map(res => res['id']),
          switchMap(subscriptionId => {
            return this.httpApi.post(
              `/accounts/${entity.accountId}/subscriptions/${subscriptionId}/valid`,
            );
          }),
          map(res => res['id']),
          switchMap(subscriptionId =>
            this.httpApi.post(
              `/user/${entity.userId}/assets/${typeUrl}`,
              _.merge(dto, { subscriptionId: subscriptionId }),
            ),
          ),
        );

    let sub: Observable<any>;
    if (entity.id != null) sub = editObservable();
    else sub = createObservable();
    return sub.pipe(
      tap(() => {
        if (reset) this?.reset();
      }),
    );
  }

  deleteAssets(entity: any, reset: boolean = true): Observable<any> {
    let typeUrl = _.camelCase(entity.type || entity.productType);
    if (
      this.assetTypeService
        .getTaxExemptingAssetTypeFipFCPI()
        .includes(entity.type?.toUpperCase())
    )
      typeUrl = 'fipfcpi';
    else if (entity.type == 'bank-resources') typeUrl = 'banking';
    else if (entity.type?.toUpperCase() === 'FPS') typeUrl = 'others';
    return this.commandAsMainUser(user =>
      this.httpApi.delete(
        `/user/${entity.userId || (user.id)}/assets/${typeUrl}/${
          entity.id
        }/physical`,
      ),
    ).pipe(
      tap(() => {
        if (reset) this?.reset();
      }),
    );
  }

  getLifeInsurranceAssets(): Observable<any> {
    return this.lifeInsurranceAssets.get();
  }

  getLifeInsurranceAssetsGlobal(): Observable<any> {
    return this.lifeInsurranceAssetsGlobal.get();
  }

  getContratCapiGlobal(): Observable<any> {
    return this.contratCapiGlobal.get();
  }

  getLifeInsurranceAsset(id): Observable<any> {
    return this.getAuthentifiedMainUserOrMainUserFromAuthentifiedSpouse().pipe(
      switchMap(user =>
        combineLatest([
          this.httpApi
            .get(`/user/${user.id}/assets/assuranceVie/${id}`)
            .pipe(map(res => res['data'])),
          this.httpApi
            .get(`/user/${user.id}/assets/assuranceVie/${id}/support`)
            .pipe(map(res => res['data'])),
        ]),
      ),
      map(([asset, repartition]) => {
        return { ...asset, ...repartition };
      }),
    );
  }

  getContratCapiAsset(id): Observable<any> {
    return this.getAuthentifiedMainUserOrMainUserFromAuthentifiedSpouse().pipe(
      switchMap(user =>
        combineLatest([
          this.httpApi
            .get(`/user/${user.id}/assets/contratCapi/${id}`)
            .pipe(map(res => res['data'])),
          this.httpApi
            .get(`/user/${user.id}/assets/contratCapi/${id}/support`)
            .pipe(map(res => res['data'])),
        ]),
      ),
      map(([asset, repartition]) => {
        return { ...asset, ...repartition };
      }),
    );
  }

  getCompteCryptoAsset(id): Observable<any> {
    return this.getAuthentifiedMainUserOrMainUserFromAuthentifiedSpouse().pipe(
      switchMap(user =>
        combineLatest([
          this.httpApi
            .get(`/user/${user.id}/assets/compteCrypto/${id}`)
            .pipe(map(res => res['data'])),
          this.httpApi
            .get(`/user/${user.id}/assets/compteCrypto/${id}/support`)
            .pipe(map(res => res['data'])),
        ]),
      ),
      map(([asset, repartition]) => {
        return { ...asset, ...repartition };
      }),
    );
  }

  getCompteCryptoSupportPerformance(assetId, supportId) {
    return this.getAuthentifiedMainUserOrMainUserFromAuthentifiedSpouse().pipe(
      mergeMap(user =>
        this.httpApi.get(
          `/user/${user.id}/assets/compteCrypto/${assetId}/support/${supportId}`,
        ),
      ),
    );
  }

  getComposedAsset(apiPath, id): Observable<any> {
    return this.getAuthentifiedMainUserOrMainUserFromAuthentifiedSpouse().pipe(
      switchMap(user =>
        combineLatest([
          this.httpApi
            .get(`/user/${user.id}/assets/${apiPath}/${id}`)
            .pipe(map(res => res['data'])),
          this.httpApi
            .get(`/user/${user.id}/assets/${apiPath}/${id}/support`)
            .pipe(map(res => res['data'])),
        ]),
      ),
      map(([asset, repartition]) => {
        return { ...asset, ...repartition };
      }),
    );
  }

  getComposedSupportPerformance(apiPath, assetId, supportId) {
    return this.getAuthentifiedMainUserOrMainUserFromAuthentifiedSpouse().pipe(
      mergeMap(user =>
        this.httpApi.get(
          `/user/${user.id}/assets/${apiPath}/${assetId}/support/${supportId}`,
        ),
      ),
    );
  }

  getAssetByType(typeUrl, id): Observable<any> {
    return this.getAuthentifiedMainUserOrMainUserFromAuthentifiedSpouse().pipe(
      switchMap(user =>
        this.httpApi
          .get(`/user/${user.id}/assets/${typeUrl}/${id}`)
          .pipe(map(res => res?.['data'])),
      ),
    );
  }

  getDefiscAsset(typeUrl, id): Observable<any> {
    return this.getAssetByType(typeUrl, id);
  }

  getPerAsset(id): Observable<any> {
    return this.getAuthentifiedMainUserOrMainUserFromAuthentifiedSpouse().pipe(
      switchMap(user =>
        combineLatest([
          this.httpApi
            .get(`/user/${user.id}/assets/per/${id}`)
            .pipe(map(res => res['data'])),
          // this.httpApi
          //   .get(`/user/${user.id}/assets/per/${id}/support`)
          //   .pipe(map((res) => res["data"])),
        ]),
      ),
      map(([asset]) => {
        return { ...asset };
      }),
    );
  }

  getPerSupportPerformance(assetId, supportId) {
    return this.getAuthentifiedMainUserOrMainUserFromAuthentifiedSpouse().pipe(
      mergeMap(user =>
        this.httpApi.get(
          `/user/${user.id}/assets/per/${assetId}/support/${supportId}`,
        ),
      ),
    );
  }
  getLifeInsurranceSupportPerformance(assetId, supportId) {
    return this.getAuthentifiedMainUserOrMainUserFromAuthentifiedSpouse().pipe(
      mergeMap(user =>
        this.httpApi.get(
          `/user/${user.id}/assets/assuranceVie/${assetId}/support/${supportId}`,
        ),
      ),
    );
  }

  getContratCapiSupportPerformance(assetId, supportId) {
    return this.getAuthentifiedMainUserOrMainUserFromAuthentifiedSpouse().pipe(
      mergeMap(user =>
        this.httpApi.get(
          `/user/${user.id}/assets/contratCapi/${assetId}/support/${supportId}`,
        ),
      ),
    );
  }

  /* Reference */
  getReferences(): Observable<any> {
    return this.references.get();
  }

  getProviderByType(
    type:
      | 'ASSURANCE_VIE'
      | 'FIP_DOMTOM'
      | 'FIP_CORSE'
      | 'PME'
      | 'CROWDFUNDING'
      | 'FCPI'
      | 'SOFICA'
      | 'GIRARDIN'
      | 'FIP'
      | 'PER'
      | 'TANGIBLE'
      | 'FPCI'
      | 'FPCR'
      | 'FCPR'
      | 'FPS',
  ): Observable<any> {
    if (!type) return of(null);
    let body: any = {
      productType: type,
      status: ['VALID'],
    };
    if (type == 'TANGIBLE')
      body = {
        productCategory: type,
        status: ['VALID'],
      };
    return this.httpApi.post(`/providers/search`, body).pipe(
      map(res =>
        _.map(res['content'], manager => {
          return _.assign(manager, { label: manager.name });
        }),
      ),
    );
  }

  getProviders(criteria?: AssetProvidersCriteria): Observable<any> {
    if (!criteria) return of(null);
    return this.httpApi.post(`/providers/search`, criteria).pipe(
      map(res =>
        _.map(res['content'], providers => {
          return _.assign(providers, { label: providers.name });
        }),
      ),
    );
  }

  getAgencesBancaires(): Observable<any> {
    return this.httpApi
      .get(`/agence-bancaire/shortcutSearch`)
      .pipe(map(res => res));
  }

  proposeManagerProvider(entity, managersList): Observable<any> {
    if (
      _.map(managersList, m => m.name.toLowerCase()).includes(
        entity.contratId.toLowerCase(),
      )
    )
      return of(null);
    let propositionDto = {
      name: entity.contratId,
      productType: entity.type,
    };
    return this.httpApi.post(`/providers/propose`, propositionDto);
  }

  getSubscriptionSources(): Observable<any> {
    return this.httpApi
      .get(`/ref/subscriptionSource`)
      .pipe(map(res => res['data']))
      .pipe(
        map(res =>
          _.map(res, source => {
            return _.assign(source, {
              label: source.label,
            });
          }),
        ),
      );
  }

  getAssetsEvolution(): Observable<any> {
    return this.getAuthentifiedMainUserOrMainUserFromAuthentifiedSpouse().pipe(
      mergeMap(user =>
        this.httpApi.get(`/user/${user.id}/assets/assuranceVie/evolution`),
      ),
      map(res => res['data']),
    );
  }

  getAssetsCapiEvolution(): Observable<any> {
    return this.getAuthentifiedMainUserOrMainUserFromAuthentifiedSpouse().pipe(
      mergeMap(user =>
        this.httpApi.get(`/user/${user.id}/assets/contratCapi/evolution`),
      ),
      map(res => res['data']),
    );
  }

  getAssetEvolution(type, id): Observable<any> {
    return this.getAuthentifiedMainUserOrMainUserFromAuthentifiedSpouse().pipe(
      mergeMap(user =>
        this.httpApi.get(`/user/${user.id}/assets/${type}/${id}/evolution`),
      ),
      map(res => res['data']),
    );
  }

  onReset: Subject<void> = new Subject<void>();

  reset() {
    // this.riskScore?.reset();
    this.assets?.reset();
    this.currentAssets?.reset();
    this.desficAssets?.reset();
    this.crowdfundingAssets?.reset();
    this.pmeAssets?.reset();
    this.othersAssets?.reset();
    this.tangibleAssets?.reset();
    this.perAssets?.reset();
    this.peaAssets?.reset();
    this.peaPmeAssets?.reset();
    this.peeAssets?.reset();
    this.percoAssets?.reset();
    this.prefonAssets?.reset();
    this.perpAssets?.reset();
    this.compteTitreAssets?.reset();
    this.compteCryptoAssets?.reset();
    this.lifeInsurranceAssets?.reset();
    this.contratCapiGlobal?.reset();
    this.bankResourcesAssets?.reset();
    this.onReset?.next();
  }

  /* state */
  onUserChange(event: any) {
    this?.reset();
  }
}
